import React, { useReducer, useState, useEffect, useRef } from 'react';

// Components
import { Modal, Form, Button, Row, Col, InputGroup } from 'react-bootstrap';
import ErrorLabel from '../../../components/ErrorLabel';

// Form Reducer
import formField from '../../../functions/formField';
import formReducer from '../../../functions/formReducer';
import formValidator from '../../../functions/formValidator';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPhoneAlt, faMobileAlt, faLockOpen } from '@fortawesome/free-solid-svg-icons';

const FormGroupWithIcon = (props) => (
    <Form.Group>
        <Form.Label>{props.label}</Form.Label>
        <InputGroup>
            <InputGroup.Prepend>
                <InputGroup.Text>
                    <FontAwesomeIcon icon={props.icon} />
                </InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control
                type="text"
                placeholder={props.placeholder}
                value={props.value}
                onChange={props.onChange}
                isInvalid={props.isInvalid}
                ref={props.firstInput} />
            <Form.Control.Feedback type="invalid">{props.errorMessage}</Form.Control.Feedback>
        </InputGroup>
    </Form.Group>
);

const FormGroupWithText = (props) => (
    <Form.Group>
        <Form.Label>{props.label}</Form.Label>
        <InputGroup>
            <InputGroup.Prepend>
                <InputGroup.Text>
                    {props.prepend}
                </InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control
                type="text"
                placeholder={props.placeholder}
                readOnly={props.readOnly}
                value={props.value}
                onChange={props.onChange}
                isInvalid={props.isInvalid} />
            <Form.Control.Feedback type="invalid">{props.errorMessage}</Form.Control.Feedback>
        </InputGroup>
    </Form.Group>
);

const CreateRepairModal = (props) => {
    const initialState = {
        customer: formField('', [3, 25]),
        device: formField('', [3, 30]),
        phone: formField('', [0, 15]),
        password: formField('', [4, 30], 'text', { required: false }),
        notes: formField('', [0, 255]),
        service: formField('', [3, 50]),
        total: formField('', [1, 7], 'number'),
        deposit: formField('', [1, 7], 'number')
    };

    // States
    const firstInput = useRef();
    const [state, dispatch] = useReducer(formReducer, initialState);
    const [owed, setOwed] = useState('0.00');
    const [errorMessage, setErrorMessage] = useState('');

    

    const getValues = () => {
        if (props.order !== null) {
            const total = +props.order.total;
            const deposit = +props.order.paid;
            const owed = (total - deposit);
            setOwed(owed.toFixed(2));

            return {
                customer: props.order.client.name,
                phone: props.order.client.phone,
                device: props.order.device.name,
                password: props.order.device.password,
                service: props.order.service,
                total: total.toFixed(2),
                deposit: deposit.toFixed(2),
                notes: props.order.notes
            }
        }

        return {};
    }

    const onSubmit = async (e) => {
        e.preventDefault();

        const isInvalid = formValidator(state, dispatch);

        if (!isInvalid && !props.editMode) {
            props.createRepair('CREATE_ORDER', {
                state: state
            }, clearRepairModal);
        } else if (props.editMode) {
            props.updateRepair('UPDATE_ORDER', {
                state: state
            }, clearRepairModal);
        } else {
            console.debug(isInvalid)
        }
    }

    const onMoneyChanged = (val, param) => {
        dispatch({ type: 'UPDATE', param: param, value: val });
    }

    const clearRepairModal = () => {
        props.onHide(); // Hide the form
        dispatch({ type: 'CLEAR', default: initialState }); // Clear the form
        setErrorMessage(''); // Clear the error message
    }

    useEffect(() => {
        // Get owed from both total and deposit
        const total = +state.total.value;
        const deposit = +state.deposit.value;
        const owed = (total - deposit);

        if (owed >= 0)
            setOwed(owed.toFixed(2));
        else
            setOwed(`(${Math.abs(owed).toFixed(2)})`)

    }, [state.total, state.deposit]);

    const focusFirstInput = () => {
        firstInput.current.focus();
    }

    return (
        <Modal
            show={props.show}
            // onShow={props.editMode ? () => dispatch({ type: 'EDIT_MODE', default: initialState, values: getValues() }) : () => dispatch({ type: 'CLEAR', default: initialState })}
            onShow={props.editMode ? () => dispatch({ type: 'EDIT_MODE', default: initialState, values: getValues() }) : () => console.debug(initialState)}
            onEntered={focusFirstInput}
            onHide={props.onHide}
            size="lg">
            <Form noValidate>
                <Modal.Header>
                    <Modal.Title>{props.editMode ? "Editar Ordén" : "Nueva Ordén"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col>
                            <FormGroupWithIcon
                                label="Nombre del Cliente"
                                firstInput={firstInput}
                                icon={faUser}
                                value={state.customer.value}
                                isInvalid={state.customer.hasError}
                                errorMessage={state.customer.errorMessage}
                                onChange={(e) => dispatch({ type: 'UPDATE', param: 'customer', value: e.target.value })} />
                        </Col>
                        <Col>
                            <FormGroupWithIcon
                                label="Numero de Telefono"
                                icon={faPhoneAlt}
                                value={state.phone.value}
                                isInvalid={state.phone.hasError}
                                errorMessage={state.phone.errorMessage}
                                onChange={(e) => dispatch({ type: 'UPDATE', param: 'phone', value: e.target.value })} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroupWithIcon
                                label="Dispositivo"
                                icon={faMobileAlt}
                                value={state.device.value}
                                isInvalid={state.device.hasError}
                                errorMessage={state.device.errorMessage}
                                onChange={(e) => dispatch({ type: 'UPDATE', param: 'device', value: e.target.value })} />
                        </Col>
                        <Col>
                            <FormGroupWithIcon
                                label="Contraseña del Dispositivo"
                                icon={faLockOpen}
                                value={state.password.value}
                                isInvalid={state.password.hasError}
                                errorMessage={state.password.errorMessage}
                                onChange={(e) => dispatch({ type: 'UPDATE', param: 'password', value: e.target.value })} />
                        </Col>
                    </Row>
                    <hr />
                    <Form.Group>
                        <Form.Label>Servicio de Reparaci&oacute;n</Form.Label>
                        <Form.Control
                            type="text"
                            value={state.service.value}
                            isInvalid={state.service.hasError}
                            onChange={(e) => dispatch({ type: 'UPDATE', param: 'service', value: e.target.value })} />
                        <Form.Control.Feedback type="invalid">{state.service.errorMessage}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Notas</Form.Label>
                        <Form.Control
                            as="textarea"
                            value={state.notes.value}
                            isInvalid={state.notes.hasError}
                            onChange={(e) => dispatch({ type: 'UPDATE', param: 'notes', value: e.target.value })} />
                        <Form.Control.Feedback type="invalid">{state.notes.errorMessage}</Form.Control.Feedback>
                    </Form.Group>
                    <Row>
                        <Col>
                            <FormGroupWithText
                                label="Total"
                                prepend="$"
                                placeholder="0.00"
                                value={state.total.value}
                                isInvalid={state.total.hasError}
                                errorMessage={state.total.errorMessage}
                                onChange={(e) => onMoneyChanged(e.target.value, 'total')} />
                        </Col>
                        <Col>
                            <FormGroupWithText
                                label="Depositado"
                                prepend="$"
                                placeholder="0.00"
                                value={state.deposit.value}
                                isInvalid={state.deposit.hasError}
                                errorMessage={state.deposit.errorMessage}
                                onChange={(e) => onMoneyChanged(e.target.value, 'deposit')}
                                readOnly={props.editMode} />
                        </Col>
                        <Col>
                            <FormGroupWithText
                                label="Debe"
                                prepend="$"
                                value={owed}
                                readOnly />
                        </Col>
                    </Row>
                    <ErrorLabel>{errorMessage}</ErrorLabel>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={props.onHide}>Cancelar</Button>
                    <Button type="submit" variant="primary" onClick={onSubmit}>
                        <span>{props.editMode ? "Guardar Ordén" : "Crear Ordén"}</span>
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default CreateRepairModal;